"use client";

import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useHeicImage } from "@/hooks/use-heic-image";
import { useImageTransformFallback } from "@/hooks/use-image-transform-fallback";
import { cn } from "@/lib/utils";
import { isHeicUrl } from "@/lib/image-url-helper";
import { CARD, imagePresetUrl, type ImagePreset } from "@/lib/images/presets";
import { SCHEDULED_LISTING_IMAGE_BLUR_CLASS } from "@/lib/scheduled-listing-blur";

/**
 * THE storage-image wrapper. One component, one way to put a stored image on
 * screen: resolve the ref → convert HEIC if needed → ask the Supabase CDN for
 * exactly the preset size → render a plain `<img>`.
 *
 * **It absorbed `ImageWithHeicFallback` (deleted 2026-07-28, handoff §9 step 8).**
 * That was a textbook fork: two parallel implementations of one job that reached
 * incompatible strategies — this one sized through the Supabase resizer, the
 * other went through the Next optimizer and re-implemented HEIC handling and a
 * blurred-placeholder crossfade on the side. They could not be reconciled while
 * `isHeicUrl` tested the URL's *suffix*, because a `…?width=…` query hid the
 * extension: a surface could have Supabase sizing OR HEIC support, never both.
 * Fixing that predicate (`lib/image-url-helper.ts`) is what let the two merge.
 * Roughly half of the deleted component was reachable only through two props
 * (`preserveDetail`, `useThumbnail`) that had zero call sites repo-wide.
 *
 * WHY A RAW `<img>` AND NOT `next/image` (handoff §8.1, §5): the `src` this
 * builds is already the exact size we want, produced by Supabase's imgproxy.
 * Handing it to the Next optimizer would buy a second server hop and a **billed
 * Vercel transformation** for the same pixels — the cache key is
 * `projectId + w + q + url + Accept`, and every MISS/STALE is charged. Measured
 * on staging: a 68 080 B source came back from `/_next/image?w=1080&q=75` at
 * 84 819 B, i.e. LARGER than the original, while imgproxy returned 51 168 B.
 *
 * `next/image` is NOT eradicated and stays correct elsewhere (handoff §11):
 * small fixed-size boxes where DPR negotiation is real work, `blob:` / `File`
 * previews in creation flows, and OG/social images that must stay full size.
 * What changed is the default and the burden of proof — reaching for the
 * optimizer now wants a comment saying why.
 *
 * NAME: historical. It renders posts, post galleries and listing cards alike —
 * anything stored in the bucket. Renaming it to `StorageImage` is a follow-up,
 * deliberately not done here: it would touch `components/feed/**` (owned by a
 * parallel agent) and `tests/unit/listing-gallery-image-urls.test.ts`, which
 * greps this file and the feed card by name.
 *
 * @see lib/images/presets.ts — the size policy this component consumes
 * @see hooks/use-heic-image.ts — the conversion funnel
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §8, §9 step 8
 */
interface ListingImageProps {
  src: string;
  alt: string;
  /**
   * The size decision, as ONE argument. Defaults to `CARD`, which is also what
   * the listing-detail slide and backdrop use — so a feed card and the detail
   * page it opens share a browser-cache entry and the second paint costs 0 B.
   *
   * It is the ONLY way to change the size, deliberately. This used to sit
   * beside `width` / `thumbHeight` escape hatches, whose one consumer
   * (`components/feed/mini-listing-card.tsx`) used them to derive six distinct
   * (width, height) pairs from a `size` variant — six cache entries per photo,
   * shared with nothing. Those props are gone: the number of distinct presets is
   * the ceiling on how many cached copies of one file can exist
   * (`lib/images/presets.ts` header), and a component-level override is a hole
   * straight through that ceiling with nothing watching it.
   *
   * A call site whose box genuinely fits no rung is a finding to raise against
   * the preset table, not a number to pass in here.
   */
  preset?: ImagePreset;
  className?: string;
  skeletonClassName?: string;
  isScheduled?: boolean;
  enableHeicConversion?: boolean;
  /**
   * Above-the-fold. The raw-`<img>` equivalent of `next/image`'s `priority`:
   * drops `loading="lazy"` and raises the fetch priority.
   */
  priority?: boolean;
  /**
   * Drives BOTH the CSS `object-fit` and the CDN `resize` mode.
   *
   * Note it is NOT defaulted from `preset.resize`, and must not be. The two
   * mean different things: `preset.resize` says how the CDN should fit the
   * source into the requested pixels, this says how the browser should fit
   * those pixels into the DOM box. They agree for a `cover` preset (`TILE`
   * crops server-side, the CSS then has nothing to do) and they deliberately
   * DISAGREE for a `contain` preset in a cropped box — `mini-listing-card` asks
   * for `CARD_SM` (aspect-preserving, so one URL serves a 3:4 box and a square
   * one) and lets `object-cover` do the crop. Defaulting this from the preset
   * would letterbox every one of those.
   *
   * Passing `objectFit="contain"` alongside a `cover` preset is the one
   * combination with no use: the CDN would crop to the box and the CSS would
   * then letterbox the result inside it. Ask for a `contain` preset instead.
   */
  objectFit?: "cover" | "contain";
}

/**
 * The three things this can be showing. One variable with three values rather
 * than the two overlapping booleans it replaces (`shouldFallback` +
 * `showSkeleton`), whose four combinations included one that means nothing.
 *
 * Derived at render from props + hook output — no state of its own, and no
 * `never` probe: a fourth variant cannot arrive without editing the five lines
 * below it, which is not what an exhaustiveness probe protects against.
 */
type ImagePhase = "fallback" | "pending" | "ready";

export function ListingImage({
  src,
  alt,
  preset = CARD,
  className,
  skeletonClassName,
  isScheduled = false,
  enableHeicConversion = true,
  priority = false,
  objectFit = "cover",
}: ListingImageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isHeic = isHeicUrl(src);
  const conversionEnabled = enableHeicConversion && (isHeic ? isVisible : true);
  const { displaySrc, isConverting, hasError } = useHeicImage(src, { enabled: conversionEnabled });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [displaySrc]);

  useEffect(() => {
    if (!enableHeicConversion) return;
    const el = containerRef.current;
    if (!el) return;
    if (!isHeic) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "250px 0px", threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enableHeicConversion, isHeic]);

  // Size the CONVERSION OUTPUT, never the raw prop. For a HEIC the hook yields a
  // `blob:` URL, which the builder passes through untouched (the bytes are
  // already decoded in memory and there is nothing for the CDN to resize); for
  // everything else `displaySrc` is the resolved public URL and the rewrite
  // applies. Sizing `src` instead would hand imgproxy a HEIC it cannot decode.
  // `imagePresetUrl`, NOT the builder with hand-passed arguments: the emitted
  // URL must be a pure function of the preset. This call used to hand the CSS
  // `objectFit` prop to the builder's CDN `resize` slot, which meant
  // `preset={TILE} objectFit="contain"` could emit a tuple no preset can
  // produce — an 11th variant per photo, through the widest image funnel in
  // the app, invisible to both fitness gates (they grep for numeric literals,
  // and this call had none). `objectFit` now drives CSS only.
  const thumbSrc = imagePresetUrl(displaySrc, preset);

  // Transform layer down -> retry once on the object endpoint underneath.
  // Without this the `onError` below just declared the image "loaded" and the
  // user got a broken icon, while the bytes sat one URL away (handoff
  // continuation §3.2). Inert on every non-transform src: `blob:` HEIC output,
  // `/placeholder.svg` and foreign hosts all yield no twin, so `report()`
  // returns false and the old behaviour is what happens.
  const { src: imgSrc, report: reportImageError } = useImageTransformFallback(thumbSrc);

  const phase: ImagePhase =
    hasError || (isHeic && !conversionEnabled)
      ? "fallback"
      : !isLoaded || isConverting
        ? "pending"
        : "ready";

  return (
    <div
      ref={containerRef}
      className={cn(
        "h-full w-full",
        objectFit === "contain"
          ? "relative flex items-center justify-center overflow-hidden bg-muted"
          : "relative",
      )}
    >
      {phase === "pending" ? (
        <Skeleton className={cn("absolute inset-0 rounded-none", skeletonClassName)} aria-hidden />
      ) : null}
      {phase === "fallback" ? (
        <div className="absolute inset-0 bg-muted" />
      ) : (
        // The raw `<img>` is the POINT of this component, not an oversight —
        // `src` is already exactly the size we want and re-optimising it would
        // cost a hop plus a billed transformation. Rationale in the header.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imgSrc}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
          className={cn(
            "transition-opacity duration-200",
            objectFit === "contain"
              ? "max-h-full max-w-full origin-center scale-[1.095] object-contain object-center"
              : "h-full w-full object-cover object-center",
            // The shared token, not a copy of its two classes: `ItemCard` used
            // to apply the same treatment itself from `lib/scheduled-listing-
            // blur.ts`, and two spellings of one design decision drift.
            isScheduled ? SCHEDULED_LISTING_IMAGE_BLUR_CLASS : "",
            phase === "pending" ? "opacity-0" : "opacity-100",
            className,
          )}
          draggable={false}
          onLoad={() => setIsLoaded(true)}
          // A retry is NOT a load. When `report()` arms the object-endpoint
          // fallback the element keeps its skeleton and waits for the second
          // URL; only when there is nothing left to try does this do what it
          // always did — call the image done so the broken icon replaces the
          // skeleton rather than sitting under it forever.
          onError={() => {
            if (!reportImageError()) setIsLoaded(true);
          }}
        />
      )}
    </div>
  );
}
